import { CrescLoader } from "../components/LoadingScreen";
import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { } from "lucide-react";
import Logo from "../components/Logo";
import PasswordStrength from "../components/PasswordStrength";

const BRAND = "#5A90F5";
const inputCls = "w-full bg-[#141414] border border-[#2A2A2A] rounded-md px-4 py-3 text-[#EDEDED] focus:border-[#5A90F5] focus:shadow-[0_0_0_3px_rgba(90,144,245,0.15)] outline-none transition-all";

export default function Register() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (password !== confirm) {
      toast.error("Passwords Do Not Match");
      return;
    }
    setLoading(true);
    try {
      await register(email, password, name);
      // Flag first-run so the Dashboard shows the 5-step welcome tour.
      localStorage.setItem("crescendo:first-run", "1");
      toast.success("Account Created");
      navigate("/app");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Signup Failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#0A0A0A] px-6">
      <div className="w-full max-w-md rounded-2xl border border-[#1F1F1F] bg-[#0F0F0F] p-8">
        <div className="flex items-center justify-center mb-5">
          <Logo size={32} />
        </div>
        <h1 className="font-display text-3xl font-semibold tracking-tight text-center">Create Account</h1>
        <p className="mt-1.5 text-xs italic tracking-wide text-center" style={{ color: BRAND }} data-testid="register-slogan">Productively, Creatively.</p>

        <form onSubmit={onSubmit} className="space-y-4 mt-6" data-testid="register-form">
          <div>
            <label className="meta-label block mb-2">NAME</label>
            <input type="text" required value={name} onChange={(e) => setName(e.target.value)}
              className={inputCls} placeholder="Your name" data-testid="register-name-input" />
          </div>
          <div>
            <label className="meta-label block mb-2">EMAIL</label>
            <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
              className={inputCls} placeholder="you@example.com" data-testid="register-email-input" />
          </div>
          <div>
            <label className="meta-label block mb-2">PASSWORD</label>
            <input type="password" required minLength={6} value={password} onChange={(e) => setPassword(e.target.value)}
              className={inputCls} placeholder="Password" autoComplete="new-password"
              data-testid="register-password-input" />
            <PasswordStrength password={password} />
          </div>
          <div>
            <label className="meta-label block mb-2">CONFIRM PASSWORD</label>
            <input type="password" required minLength={6} value={confirm} onChange={(e) => setConfirm(e.target.value)}
              className={`${inputCls} ${confirm && confirm !== password ? "border-[#FF3B30]" : ""}`}
              placeholder="Repeat your password" autoComplete="new-password"
              data-testid="register-confirm-input" />
            {confirm && confirm !== password && (
              <p className="mt-1.5 text-[11px] text-[#FF3B30]" data-testid="password-mismatch">Passwords don't match yet.</p>
            )}
          </div>
          <button type="submit" disabled={loading}
            className="w-full font-medium py-3 rounded-md transition-all flex items-center justify-center gap-2 disabled:opacity-60 hover:shadow-[0_0_24px_0_rgba(90,144,245,0.45)] text-white"
            style={{ background: BRAND }}
            data-testid="register-submit-btn">
            {loading && <CrescLoader size={16} />}
            Create Account
          </button>
        </form>

        <div className="flex items-center gap-3 my-6">
          <div className="flex-1 h-px bg-[#2A2A2A]" />
          <span className="meta-label">OR</span>
          <div className="flex-1 h-px bg-[#2A2A2A]" />
        </div>

        <button
          type="button"
          onClick={async () => {
            try {
              const { api } = await import("../lib/api");
              const redirectUri = window.location.origin + "/auth/callback";
              const { data } = await api.get(`/auth/google/url?redirect_uri=${encodeURIComponent(redirectUri)}`);
              window.location.href = data.url;
            } catch {
              toast.error("Google sign-in unavailable. Use email/password.");
            }
          }}
          className="w-full bg-[#141414] border border-[#2A2A2A] text-[#EDEDED] py-2.5 rounded-md hover:bg-[#1a1a1a] hover:border-[#3A3A3A] transition-all flex items-center justify-center gap-3 text-sm"
          data-testid="google-signup-btn"
        >
          <svg className="w-4 h-4" viewBox="0 0 24 24">
            <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
            <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
            <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
            <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
          </svg>
          Continue with Google
        </button>

        <p className="text-center text-xs text-[#A1A1AA] mt-6">
          Have An Account?{" "}
          <Link to="/login" className="text-[#EDEDED] underline underline-offset-2">Sign In</Link>
        </p>
      </div>
    </div>
  );
}
